import * as requestParamTypes from '../../../../consts/app/requestParamTypes';
import dataCollectorWorkModes from '../../../../consts/dataCollector/dataCollectorWorkModes.ts';
import DeviceGroupingTypes from '../../../../consts/devices/deviceGroupingTypes.ts';
import DeviceTypes from '../../../../consts/devices/devicesTypes.ts';
import * as formInputNames from '../../../../consts/form/formInputNames';
import * as formItemTypes from '../../../../consts/form/formItemTypes';
import {groupingTypeLocalizations} from '../../../../consts/localization/groupingTypeLocalizations';
import * as localizationKeys from '../../../../consts/localization/localizationKeys';
import * as modalTypes from '../../../../consts/modals/modalTypes';
import buildingsService from '../../../../services/buildings/buildingsService';
import deviceService from '../../../../services/device/deviceService';
import {getLocalizedStrings} from '../../../../services/localization/localizationService';
import {makeSelectBuildingDataCollectors, makeSelectRooms} from '../../../../state/selectors/buildings';
import {
    makeSelectDeviceInfo,
    makeSelectDeviceModels,
    makeSelectDeviceProfileTypes,
    makeSelectDevices,
    makeSelectDeviceTypes,
} from '../../../../state/selectors/device';
import {getState} from '../../../../state/store';
import {filterModelsByWorkMode} from '../../../../utils/deviceModels';

const getFormContent = (modalType, buildingID, propRoomID, isEditing, currentWorkMode) => {
    const deviceInfo = makeSelectDeviceInfo()(getState());

    const devices = makeSelectDevices()(getState());

    const isAddDeviceModalType = modalTypes.ADD_DEVICE_MODAL_TYPE === modalType;
    const isGateway = currentWorkMode === dataCollectorWorkModes.Gateway;

    const {
        manualID,
        factoryID,
        waterDeviceID,
        groups,
        groupID,
        groupingType,
        parentID,
        serviceDate,
        uspdID,
        deviceID,
        modelID,
        roomName,
        uspdName,
        parentName,
        roomID,
        models,
        description,
        appKey,
        joinEui,
        devEui,
    } = deviceInfo || {};

    const localizedStrings = getLocalizedStrings();

    const title = isAddDeviceModalType
        ? localizedStrings[localizationKeys.INDICATOR_MODAL_ADD_FORM_TITLE]
        : localizedStrings[localizationKeys.INDICATOR_MODAL_EDIT_FORM_TITLE];

    const submitButton = isAddDeviceModalType
        ? localizedStrings[localizationKeys.INDICATOR_MODAL_ADD_FORM_SUBMIT_BUTTON]
        : localizedStrings[localizationKeys.INDICATOR_MODAL_EDIT_FORM_SUBMIT_BUTTON];

    const cancelButton = localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_CANCEL_BUTTON];

    const groupingTypes = [
        {
            value: groupingTypeLocalizations[DeviceGroupingTypes[DeviceGroupingTypes.Grouped]],
            key: DeviceGroupingTypes.Grouped,
        },
        {
            value: groupingTypeLocalizations[DeviceGroupingTypes[DeviceGroupingTypes.Common]],
            key: DeviceGroupingTypes.Common,
        },
        {
            value: groupingTypeLocalizations[DeviceGroupingTypes[DeviceGroupingTypes.Distributed]],
            key: DeviceGroupingTypes.Distributed,
        },
    ];

    const getInitialValue = (arr, findKey) => arr?.find((item) => item?.key === findKey)?.value;

    const initialDeviceTypeGroupsValue = getInitialValue(groups, groupID);
    const initialGroupingTypeValue = getInitialValue(groupingTypes, groupingType);

    const modelName = models?.find((e) => e.key === modelID)?.value;

    const roomId = roomID || propRoomID;

    const initialValues = {
        [formInputNames.BUILDING_ID]: buildingID,
        [formInputNames.MANUAL_ID]: manualID,
        [formInputNames.DEV_EUI]: devEui,
        [formInputNames.FACTORY_ID]: factoryID,
        [formInputNames.WATER_DEVICE_ID]: waterDeviceID,
        [formInputNames.APP_KEY]: appKey,
        [formInputNames.JOIN_EUI]: joinEui,
        [formInputNames.DEVICE_TYPE_ID]: groupID &&
            initialDeviceTypeGroupsValue && {
                value: initialDeviceTypeGroupsValue,
                id: groupID,
            },
        [formInputNames.GROUPING_TYPE]:
            propRoomID && !isEditing
                ? {
                      value: getInitialValue(groupingTypes, DeviceGroupingTypes.Distributed),
                      id: DeviceGroupingTypes.Distributed,
                  }
                : groupingType && {
                      value: initialGroupingTypeValue,
                      id: groupingType,
                  },
        [formInputNames.PARENT_DEVICE_ID]: parentID && {
            value: parentName,
            id: parentID,
        },
        [formInputNames.SERVICE_DATE]: serviceDate,
        [formInputNames.ROOM_ID]:
            propRoomID && !isEditing
                ? roomId
                : roomId && {
                      value: roomName,
                      id: roomId,
                  },
        [formInputNames.PZPD_ID]: uspdID && {
            value: uspdName || '',
            id: uspdID,
        },
        [formInputNames.DEVICE_MODEL_ID]: modelID &&
            modelName && {
                value: modelName,
                id: modelID,
            },
        deviceID,
        [formInputNames.DEVICE_DESCRIPTION]: description,
    };

    const requireDeps = formInputNames.GROUPING_TYPE;

    const formFields = [
        {
            key: formInputNames.BUILDING_ID,
            name: formInputNames.BUILDING_ID,
            required: false,
            hidden: true,
        },
        {
            key: formInputNames.MANUAL_ID,
            name: formInputNames.MANUAL_ID,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_MANUAL_ID_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_MANUAL_ID_FIELD_PLACEHOLDER],
            required: true,
            pattern: /^[0-9]{8}$/,
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
            feedbackContent: [localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_MANUAL_ID_FIELD_FEEDBACK_RULE_1]],
        },
        {
            key: formInputNames.SERVICE_DATE,
            name: formInputNames.SERVICE_DATE,
            withDisabledDate: false,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_SERVICE_DATE_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_SERVICE_DATE_FIELD_PLACEHOLDER],
            formItemType: formItemTypes.DATE_PICKER,
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
        },
        {
            key: formInputNames.PZPD_ID,
            name: formInputNames.PZPD_ID,
            formItemType: formItemTypes.SELECT,
            fieldLabel: 'value',
            fieldValue: 'value',
            itemDeps: formInputNames.GROUPING_TYPE,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_PZPD_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_PZPD_FIELD_PLACEHOLDER],
            required: true,
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
            requestOptions: {
                selectRequestData: makeSelectBuildingDataCollectors,
                getRequestData: () => buildingsService.getDataCollectorByBuildingId(buildingID),
            },
        },
        {
            key: formInputNames.DEV_EUI,
            name: formInputNames.DEV_EUI,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEV_EUI_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEV_EUI_FIELD_PLACEHOLDER],
            formItemType: formItemTypes.INPUT,
            itemDeps: formInputNames.PZPD_ID,
            hidden: () => !isGateway,
            required: () => isGateway,
            disabled: isEditing && !!devEui,
            pattern: /^[a-fA-F0-9]{16}$/,
            errorMessage: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEV_EUI_FIELD_ERROR],
        },
        {
            key: formInputNames.GROUPING_TYPE,
            name: formInputNames.GROUPING_TYPE,
            fieldLabel: 'value',
            fieldValue: 'key',
            disabled: propRoomID && !isEditing,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_GROUPING_TYPE_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_GROUPING_TYPE_FIELD_PLACEHOLDER],
            formItemType: formItemTypes.SELECT,
            required: true,
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
            requestOptions: {
                options: groupingTypes,
            },
        },
        {
            key: formInputNames.DEVICE_TYPE_ID,
            name: formInputNames.DEVICE_TYPE_ID,
            fieldLabel: 'value',
            fieldValue: 'key',
            formItemType: formItemTypes.SELECT,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEVICE_TYPE_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEVICE_TYPE_FIELD_PLACEHOLDER],
            required: true,
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
            requestOptions: {
                selectRequestData: isAddDeviceModalType ? makeSelectDeviceTypes : makeSelectDeviceProfileTypes,
                getRequestData: () => deviceService.getDeviceTypesGroups(),
            },
        },
        {
            key: formInputNames.DEVICE_MODEL_ID,
            name: formInputNames.DEVICE_MODEL_ID,
            fieldLabel: 'value',
            fieldValue: 'key',
            formItemType: formItemTypes.SELECT,
            required: (_, deviceType) => Boolean(deviceType?.id),
            disabled: (_, deviceType) => !deviceType?.id,
            itemDeps: formInputNames.DEVICE_TYPE_ID,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEVICE_MODEL_ID_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEVICE_MODEL_ID_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
            requestOptions: {
                selectRequestData: () => (state) => {
                    if (!state) return [];

                    const data = makeSelectDeviceModels()(state);

                    if (!data) return [];

                    const isArray = Array.isArray(data);
                    const items = isArray ? data : data.items || [];
                    const filtered = filterModelsByWorkMode(items, currentWorkMode);

                    return isArray ? filtered : {...data, items: filtered};
                },
                getRequestData: () => deviceService.getDevicesModelsByGroupID(groupID),
            },
        },
        {
            key: formInputNames.PARENT_DEVICE_ID,
            name: formInputNames.PARENT_DEVICE_ID,
            allowClear: true,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_PARENT_DEVICE_ID_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_PARENT_DEVICE_ID_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
            formItemType: formItemTypes.SELECT,
            itemDeps: formInputNames.PZPD_ID,
            fieldValue: 'value',
            fieldLabel: 'value',
            disabled: (groupingType, dataCollector) => {
                const isCommonDevice = groupingType?.id === DeviceGroupingTypes.Common;

                return !dataCollector || (isCommonDevice && (isAddDeviceModalType || isEditing || !parentID));
            },
            requestOptions: {
                options: Array.isArray(devices?.items) ? devices.items.filter((device) => device.key !== parentID) : [],
                selectRequestData: makeSelectDevices,
                getRequestData: () =>
                    deviceService.getDevices({buildingId: buildingID, dataCollectorId: uspdID, groupingType}),
            },
        },
        propRoomID && !isEditing
            ? {
                  key: formInputNames.ROOM_ID,
                  name: formInputNames.ROOM_ID,
                  required: false,
                  hidden: true,
              }
            : {
                  key: formInputNames.ROOM_ID,
                  name: formInputNames.ROOM_ID,
                  required: (_, groupingType) => groupingType?.id === DeviceGroupingTypes.Distributed,
                  fieldLabel: 'value',
                  fieldValue: 'value',
                  withSearch: true,
                  allowClear: true,
                  formItemType: formItemTypes.SELECT,
                  itemDeps: formInputNames.GROUPING_TYPE,
                  disabled: (_, groupingType) => groupingType?.id !== DeviceGroupingTypes.Distributed,
                  label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_ROOM_ID_FIELD_LABEL],
                  placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_ROOM_ID_FIELD_PLACEHOLDER],
                  errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
                  requestOptions: {
                      paramsType: requestParamTypes.ROOMS,
                      selectRequestData: makeSelectRooms,
                      getRequestData: () => buildingsService.getRoomsByBuildingId(buildingID),
                  },
              },
        {
            key: formInputNames.FACTORY_ID,
            name: formInputNames.FACTORY_ID,
            itemDeps: formInputNames.DEVICE_TYPE_ID,
            hidden: (_, deviceType) =>
                deviceType?.id !== DeviceTypes.HotWater &&
                deviceType?.id !== DeviceTypes.ColdWater &&
                deviceType?.id !== DeviceTypes.Water &&
                deviceType?.id !== DeviceTypes.Sewage,

            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_FACTORY_ID_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_FACTORY_ID_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
        },
        {
            key: formInputNames.WATER_DEVICE_ID,
            name: formInputNames.WATER_DEVICE_ID,
            itemDeps: formInputNames.DEVICE_TYPE_ID,
            hidden: (_, deviceType) => {
                return (
                    deviceType?.id !== DeviceTypes.HotWater &&
                    deviceType?.id !== DeviceTypes.ColdWater &&
                    deviceType?.id !== DeviceTypes.Water &&
                    deviceType?.id !== DeviceTypes.Sewage
                );
            },
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_ADDITIONAL_MANUAL_ID_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_ADDITIONAL_MANUAL_ID_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.BUILDING_FORM_REQUIRED_ERROR],
        },
        {
            key: formInputNames.JOIN_EUI,
            name: formInputNames.JOIN_EUI,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_JOIN_EUI_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_JOIN_EUI_FIELD_PLACEHOLDER],
            formItemType: formItemTypes.INPUT,
            itemDeps: formInputNames.PZPD_ID,
            hidden: () => !isGateway,
            required: () => isGateway,
            pattern: /^[a-fA-F0-9]{16}$/,
            errorMessage: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_JOIN_EUI_FIELD_ERROR],
        },
        {
            key: formInputNames.APP_KEY,
            name: formInputNames.APP_KEY,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_APP_KEY_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_APP_KEY_FIELD_PLACEHOLDER],
            formItemType: formItemTypes.INPUT,
            itemDeps: formInputNames.PZPD_ID,
            hidden: () => !isGateway,
            required: () => isGateway,
            pattern: /^[a-fA-F0-9]{32}$/,
            errorMessage: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_APP_KEY_FIELD_ERROR],
        },
        {
            key: formInputNames.DEVICE_DESCRIPTION,
            name: formInputNames.DEVICE_DESCRIPTION,
            label: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEVICE_DESCRIPTION_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.INDICATOR_MODAL_FORM_DEVICE_DESCRIPTION_FIELD_PLACEHOLDER],
            formItemType: formItemTypes.INPUT,
        },
    ];

    return {formFields, title, submitButton, cancelButton, initialValues, requireDeps};
};

export default {
    getFormContent,
};
